import * as userActions from './actions';

import { catchError, filter, map, mapTo, switchMap, withLatestFrom } from 'rxjs/operators';

import { AjaxError } from 'rxjs/ajax';
import { Epic } from 'redux-observable';
import { RootAction } from 'store/actions';
import { RootDependencies } from 'store/dependencies';
import { RootState } from 'store/reducer';
import { UnauthorizedError } from 'store/user/errors';
import { isActionOf } from 'typesafe-actions';
import { of } from 'rxjs';
import { replace } from 'connected-react-router';
import { replaceSearchString } from 'store/user/utils';

const UNAUTHORIZED_ERROR_CODE = 401;
const NOT_FOUND_ERROR_CODE = 404;
const BAD_REQUEST_ERROR_CODE = 400;
const INTERNAL_SERVER_ERROR_CODE = 500;

const isUnauthorizedError = (error: unknown): boolean =>
  error instanceof AjaxError && error.status >= BAD_REQUEST_ERROR_CODE && error.status < INTERNAL_SERVER_ERROR_CODE;

export const externalLogin: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(userActions.externalLogin.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) => {
      const { authToken, apiUrl } = payload;

      return apiClient(state, apiUrl, authToken)
        .getCurrentUser()
        .pipe(
          map(xhrPayload => {
            return userActions.externalLogin.success({ user: xhrPayload.response, apiUrl, token: authToken });
          }),
          catchError((error: Error) => {
            if (isUnauthorizedError(error)) {
              return of(userActions.externalLogin.failure({ error: new UnauthorizedError() }));
            }

            return of(userActions.externalLogin.failure({ error }));
          }),
        );
    }),
  );

export const externalLoginRedirect: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.pipe(
    filter(isActionOf(userActions.externalLogin.success)),
    withLatestFrom(state$),
    map(([, state]) => {
      const search: string = state.router?.location?.search ?? '';
      const path = state.router?.location?.pathname ?? '/';
      return replaceSearchString(search, path);
    }),
  );

export const postMessageLoginRedirect: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.pipe(
    filter(isActionOf(userActions.postMessageLogin)),
    withLatestFrom(state$),
    map(([, state]) => {
      const search: string = state.router?.location?.search ?? '';
      const path = state.router?.location?.pathname ?? '/';
      return replaceSearchString(search, path);
    }),
  );

export const externalLoginFailureRedirect: Epic<RootAction, RootAction, RootState> = action$ =>
  action$.pipe(filter(isActionOf(userActions.externalLogin.failure)), mapTo(replace('/')));

export const getCurrentUser: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(userActions.getCurrentUser.request)),
    withLatestFrom(state$),
    switchMap(([, state]) =>
      apiClient(state)
        .getCurrentUser()
        .pipe(
          map(xhrPayload => {
            return userActions.getCurrentUser.success({ user: xhrPayload.response });
          }),
          catchError((error: Error) => {
            if (
              error instanceof AjaxError &&
              (error.status === UNAUTHORIZED_ERROR_CODE || error.status === NOT_FOUND_ERROR_CODE)
            ) {
              return of(userActions.getCurrentUser.failure({ error: new UnauthorizedError() }));
            }

            return of(userActions.getCurrentUser.failure({ error }));
          }),
        ),
    ),
  );
